import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import CallToAction from '../components/CallToAction';

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-neutral last:border-b-0">
      <button
        className="flex justify-between items-center w-full py-5 text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-xl font-semibold">{question}</h3>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="text-secondary-light">{answer}</div>
      </div>
    </div>
  );
};

const FaqPage = () => {
  const [activeItem, setActiveItem] = useState(null);
  
  useEffect(() => {
    document.title = "Frequently Asked Questions | Snap Launch Studio Website Process";
  }, []);

  const faqs = [
    {
      question: "How is this different from other web designers?",
      answer: "Unlike most web designers who start with a sales pitch and ask for money upfront, we build your website first, then show you what's possible. This approach eliminates risk for you and ensures you can see exactly what you're getting before making a full commitment. We also specialize specifically in contractor and service business websites, so we understand your industry and customers better than general web designers."
    },
    {
      question: "What if I don't like the website you build?",
      answer: "Our 100% money-back guarantee means if you're not completely satisfied with your website, we'll refund your $500 deposit. We'll also revise the site based on your feedback to ensure it meets your expectations. We don't consider the project complete until you're happy with the result."
    },
    {
      question: "How long does the process really take?",
      answer: "From start to finish, we deliver completed websites in 14 days. This includes a first draft within 7 days, time for your feedback and revisions, and final delivery. This timeline is significantly faster than most web design agencies, which typically take 6-8 weeks or longer."
    },
    {
      question: "What happens after my website is launched?",
      answer: "Once your website is launched, it's yours - no monthly fees or ongoing contracts required for the basic website. If you want ongoing support, we offer optional maintenance packages. We also provide documentation so you can make basic updates yourself if you prefer. For additional services like SEO optimization or content updates, we offer those as separate services."
    },
    {
      question: "Do you work with businesses outside of contracting?",
      answer: "While we specialize in websites for contractors and service businesses (plumbers, electricians, HVAC, landscapers, etc.), we occasionally work with other small local businesses that have similar needs. Our process and approach work best for service-based businesses looking to generate leads online."
    },
    {
      question: "What's included in the $1,500 price?",
      answer: "Our core package includes a custom-designed website (not a template), mobile optimization, contact forms and lead capture, Google Analytics integration, multiple revisions, and complete website delivery within 14 days. The price covers everything needed for a professional, functional website that's ready to generate leads for your business."
    },
    {
      question: "Do I need to provide content and images?",
      answer: "While you're welcome to provide specific content and images if you have them, we don't require it. We can create professional content based on information about your business and services. We also source high-quality images appropriate for your industry. Of course, if you have specific photos of your work or team, we're happy to incorporate those."
    },
    {
      question: "Will my website work on mobile phones?",
      answer: "Absolutely! All our websites are built with a mobile-first approach, ensuring they look and function perfectly on smartphones, tablets, and desktop computers. This is especially important since the majority of contractor searches happen on mobile devices. Your site will automatically adjust to provide the best experience on any screen size."
    },
    {
      question: "Can you help with SEO and getting found on Google?",
      answer: "Yes! All our websites include basic on-page SEO as part of the core package. For businesses wanting more advanced search engine optimization, we offer additional SEO services including local SEO setup, Google Business Profile optimization, keyword research, and content optimization for specific service areas."
    },
    {
      question: "What if I need changes after launch?",
      answer: "We provide documentation so you can make basic content updates yourself if you'd like. For more substantial changes or ongoing updates, we offer hourly rates for modifications or monthly maintenance packages. We're always available to help with your website, even long after the initial launch."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Common Questions About Our Process" 
      />
      
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="glass-card p-8">
              {faqs.map((faq, index) => (
                <FaqItem 
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction title="Still have questions? Let's talk" />
    </>
  );
};

export default FaqPage;
